import React, { useEffect, useState } from "react";
import { mdiPlus } from "@mdi/js";
import { ServicePOApiModel } from "../../../purchase_order/purchase_order_create/models/po_service_model";
import { fetchGet } from "../../../../service/api_client";
import ServiceReceiptTabs from "./service/service_receipt_tab";
import { urls } from "../../../../utils/urls";
import { useHistory } from "react-router-dom";
import {
  ResponsiveListShell,
  StatusBadge,
  MobileRecordCard,
  MobileRecordCardList,
  EmptyState,
} from "../../../../ui/new_components/common/responsive";
import Pagination from "../../../../ui/new_components/common/pagination";
import LoadingWidget from "../../../../context_providers/modal/loader";

const ServiceReceiptPage: React.FC = () => {
  const [data, setData] = useState<ServicePOApiModel[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [total, setTotal] = useState(0);
  const [fetchedAt, setFetchedAt] = useState<Date>();
  const [activeTab, setActiveTab] = useState<"SERVICE_POS" | "SRN_HISTORY">(
    "SERVICE_POS",
  );
  const navigate = useHistory();
  const [search, setSearch] = useState("");

  const fetchServicePOs = async () => {
    try {
      setLoading(true);

      let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/get-service-pos?page=${page}&count=${limit}`;
      if (search) {
        url += `&search=${search}`;
      }

      const result = await fetchGet(url);

      setData(result.data.data);
      setTotal(result.data.meta.total);
      setFetchedAt(new Date());
    } catch (error) {
      console.error("Failed to fetch service POs", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicePOs();
  }, [page, limit, search]);

  const createSRN = (po: ServicePOApiModel) =>
    navigate.push(`${urls.purchase_service_voucher}/create?po_no=${po.po_no}`, {
      search: `?po_no=${po.po_no}`,
      state: { type: "Service" },
    });

  const servicesNode = (po: ServicePOApiModel) => (
    <div className="space-y-1">
      {po.items.slice(0, 2).map((i, idx) => (
        <div
          key={idx}
          className="text-gray-700 whitespace-normal break-words"
          title={i.item.name}
        >
          • {i.item.name}
        </div>
      ))}
      {po.items.length > 2 && (
        <div className="text-xs text-gray-500">+{po.items.length - 2} more</div>
      )}
    </div>
  );

  return (
    <>
      <ResponsiveListShell
        title="Service Receipt Note"
        count={total}
        fetchedAt={fetchedAt}
        onRefresh={fetchServicePOs}
      >
        <div className="mt-2">
          <ServiceReceiptTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            search={search}
            onSearchChange={setSearch}
          />
        </div>

        {data.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table className="w-full text-xs divide-y divide-gray-200 relative">
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        PO Number
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Project
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Services
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Status
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((po) => (
                      <tr className="border" key={po.po_no}>
                        <td className="px-3 py-3 whitespace-nowrap text-left font-medium">
                          {po.po_no}
                        </td>
                        <td className="px-3 py-3 text-left">
                          {po.items[0]?.project?.name ?? "-"}
                        </td>
                        <td className="px-3 py-3 text-left max-w-md">
                          {servicesNode(po)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-left">
                          <StatusBadge status={po.status} kind="generic" />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-right">
                          <button
                            className="px-4 py-2 text-xs bg-myPrimaryColor hover:bg-mySecondaryColor text-white rounded-lg"
                            onClick={() => createSRN(po)}
                          >
                            Create SRN
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((po) => (
                <MobileRecordCard
                  key={po.po_no}
                  onClick={() => createSRN(po)}
                  title={po.po_no}
                  status={<StatusBadge status={po.status} kind="generic" />}
                  fields={[
                    {
                      label: "Project",
                      value: po.items[0]?.project?.name ?? "-",
                    },
                    { label: "Services", value: servicesNode(po) },
                  ]}
                  actions={[
                    {
                      icon: mdiPlus,
                      title: "Create SRN",
                      label: "Create SRN",
                      onClick: () => createSRN(po),
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
              <Pagination
                itemCount={data.length}
                page={page}
                total={total}
                count={limit}
                onChange={(p) => setPage(p)}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No Service POs found!"
            message="Try adjusting your search."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default ServiceReceiptPage;
